// src/pages/PaySuccess.jsx
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { saveSubscription } from "../utils/subscription";
import { PLANS } from "../config/payments";

export default function PaySuccess() {
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const back = query.get("back") || "/chapter-4";
  const planKey = (query.get("plan") || "").toLowerCase(); // day_inr / week_inr / annual_inr
  const tenure = parseInt(query.get("tenure") || "0", 10) || 0;

  const paymentId = query.get("razorpay_payment_id");
  const paymentLinkId = query.get("razorpay_payment_link_id");
  const paymentStatus = (
    query.get("razorpay_payment_link_status") ||
    query.get("payment_status") ||
    ""
  ).toLowerCase();
  const signature = query.get("razorpay_signature") || "";

  React.useEffect(() => {
    // If we have neither ids nor an explicit paid status, send user back to subscribe
    if (!paymentId && !paymentLinkId && paymentStatus !== "paid") {
      navigate(`/subscribe?back=${encodeURIComponent(back)}`, { replace: true });
      return;
    }

    const run = async () => {
      try {
        // Ask server to verify and set the signed cookie
        const url = new URL("/api/verify-payment", window.location.origin);
        if (paymentId) url.searchParams.set("payment_id", paymentId);
        if (paymentLinkId) url.searchParams.set("plink_id", paymentLinkId);
        if (signature) url.searchParams.set("signature", signature);
        if (paymentStatus) url.searchParams.set("status", paymentStatus);
        if (planKey) url.searchParams.set("plan", planKey);
        if (tenure) url.searchParams.set("tenure", String(tenure));

        const res = await fetch(url.toString(), { credentials: "include" });
        const data = await res.json().catch(() => ({}));

        if (res.ok && data?.ok) {
          // Also persist a friendly local cache (your guard still trusts the cookie)
          const plan =
            (PLANS.DAY_PASS.id.toLowerCase() === planKey && PLANS.DAY_PASS) ||
            (PLANS.WEEK_PASS.id.toLowerCase() === planKey && PLANS.WEEK_PASS) ||
            (PLANS.ANNUAL_INR.id.toLowerCase() === planKey && PLANS.ANNUAL_INR) ||
            { id: planKey || "paid", tenureDays: tenure || 7 };

          saveSubscription({
            planId: plan.id,
            paymentId: paymentId || paymentLinkId || "verified",
            email: "",
            tenureDays: plan.tenureDays,
          });

          // tiny delay to ensure storage/cookie is ready before we re-enter guards
          setTimeout(() => {
            navigate(back, { replace: true, state: { reason: "paid_ok" } });
          }, 50);
          return;
        }

        console.error("Verify failed:", data);
        navigate(`/subscribe?need=verify&back=${encodeURIComponent(back)}`, { replace: true });
      } catch (e) {
        console.error(e);
        navigate(`/subscribe?need=network&back=${encodeURIComponent(back)}`, { replace: true });
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-700">Confirming your payment…</p>
      </div>
    </div>
  );
}
